/*
  <otw-facets> – filtering, sorting and pagination for collection and search listings (snippets/otw-listing.liquid).

  Every change builds a query string, fetches `?{params}&section_id=<section>` (Section Rendering API) and swaps the
  `[data-region]` elements: bar, sidebar, pills, sort, sort-sheet, drawer-form, count-text, grid, pagination.
  The URL is updated with pushState (replaceState for load more / infinite) and popstate restores the state.
  Mobile drawer selections are staged until Apply; the live count comes from the `otw-facet-count` section.
  On failure the page falls back to a normal navigation. Shares state with other modules through window.OTW only.
*/
const SKIP_PARAMS = ['section_id', 'sections', 'sections_url'];
const mobileQuery = window.matchMedia('(max-width: 989px)');

function cleanParams(search) {
  const params = new URLSearchParams(search);
  SKIP_PARAMS.forEach((key) => params.delete(key));
  return params;
}

function paramsFromLink(anchor) {
  return cleanParams(new URL(anchor.getAttribute('href'), window.location.origin).search);
}

class OtwFacets extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.sectionId = this.dataset.sectionId;
    this.mode = this.dataset.pagination || 'numbered';
    this.controller = null;
    this.countController = null;
    this.loading = false;
    this.pendingCount = null;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('input', (event) => this.onInput(event));
    this.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    this.onPopState = () => this.apply(cleanParams(window.location.search), { push: false, scroll: false });
    window.addEventListener('popstate', this.onPopState);

    this.onDocClick = (event) => {
      if (!event.target.closest('.otw-sort')) this.closeSort();
    };
    document.addEventListener('click', this.onDocClick);

    this.syncChips();
    this.observeInfinite();
    this.watchHeader();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('click', this.onDocClick);
    this.infiniteObserver?.disconnect();
    this.headerObserver?.disconnect();
  }

  /* ---------- State ---------- */
  get params() {
    return cleanParams(window.location.search);
  }

  get total() {
    return Number(this.querySelector('[data-region="count-text"]')?.dataset.total || 0);
  }

  paramsFromForm(form) {
    const params = this.params;
    Array.from(params.keys())
      .filter((key) => key.startsWith('filter.'))
      .forEach((key) => params.delete(key));
    params.delete('page');
    Array.from(form.elements).forEach((el) => {
      if (!el.name || el.disabled || el.value === '') return;
      if (el.type === 'checkbox' && !el.checked) return;
      if (el.type === 'radio') return;
      if (el.matches('[data-price-min]') && Number(el.value) <= 0) return;
      if (el.matches('[data-price-max]')) {
        const max = Number(el.closest('[data-price-range]')?.dataset.rangeMax || 0);
        if (max && Number(el.value) >= max) return;
      }
      params.append(el.name, el.value);
    });
    return params;
  }

  /* ---------- Events ---------- */
  onClick(event) {
    const target = event.target;

    const link = target.closest('[data-facet-link]');
    if (link && this.contains(link) && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      const params = paramsFromLink(link);
      const paged = Boolean(link.closest('.otw-pagination'));
      this.apply(params, { scroll: true, force: paged });
      return;
    }

    const chip = target.closest('[data-chip]');
    if (chip) {
      this.toggleChip(chip.dataset.chip);
      return;
    }

    if (target.closest('[data-sort-toggle]')) {
      this.toggleSort();
      return;
    }

    const sortOption = target.closest('[data-sort-value]');
    if (sortOption) {
      this.closeSort(true);
      this.setSort(sortOption.dataset.sortValue);
      return;
    }

    const more = target.closest('[data-fmore]');
    if (more) {
      const list = more.previousElementSibling;
      const expanded = list.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', String(expanded));
      more.textContent = expanded ? more.dataset.less : more.dataset.more;
      return;
    }

    const tab = target.closest('[data-group-tab]');
    if (tab) {
      this.selectGroup(tab);
      return;
    }

    if (target.closest('[data-facet-clear]')) {
      this.clearStaged();
      return;
    }

    const loadMore = target.closest('[data-load-more]');
    if (loadMore) {
      event.preventDefault();
      this.loadMore();
      return;
    }

    const opener = target.closest('[data-drawer-open]');
    if (opener && opener.dataset.drawerOpen.startsWith('OtwFilter-')) {
      this.querySelector('[data-facet-form="drawer"]')?.reset();
      this.syncSlidersFromInputs(this.querySelector('[data-facet-form="drawer"]'));
      this.refreshStaged({ fetchCount: false });
    }
  }

  onInput(event) {
    const range = event.target.closest('[data-range-min], [data-range-max]');
    if (range) this.syncNumbersFromSliders(range);
  }

  onChange(event) {
    const target = event.target;

    if (target.matches('[data-sort-radio]')) {
      this.querySelector('otw-drawer.otw-sortsheet')?.close?.();
      this.setSort(target.value);
      return;
    }

    const form = target.closest('[data-facet-form]');
    if (!form) return;

    if (target.matches('[data-price-min], [data-price-max]')) this.syncSlidersFromInputs(form, target);

    if (form.dataset.facetForm === 'sidebar') {
      this.apply(this.paramsFromForm(form));
    } else {
      this.refreshStaged();
    }
  }

  onSubmit(event) {
    const form = event.target.closest('[data-facet-form]');
    if (!form) return;
    event.preventDefault();
    this.apply(this.paramsFromForm(form), { scroll: true });
    if (form.dataset.facetForm === 'drawer') this.querySelector('otw-drawer.otw-fdrawer')?.close?.();
  }

  onKeydown(event) {
    const tab = event.target.closest('[data-group-tab]');
    if (tab && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const tabs = Array.from(this.querySelectorAll('[data-group-tab]'));
      let index = tabs.indexOf(tab);
      if (event.key === 'ArrowDown') index = (index + 1) % tabs.length;
      if (event.key === 'ArrowUp') index = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = tabs.length - 1;
      this.selectGroup(tabs[index], true);
      return;
    }

    const menu = event.target.closest('[data-sort-menu]');
    const toggle = event.target.closest('[data-sort-toggle]');
    if (event.key === 'Escape' && (menu || toggle)) {
      event.stopPropagation();
      this.closeSort(true);
      return;
    }
    if ((menu || toggle) && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      const root = this.querySelector('.otw-sort');
      if (root.querySelector('[data-sort-menu]').hidden) this.toggleSort();
      const options = Array.from(root.querySelectorAll('[data-sort-value]'));
      const index = options.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      options[(next + options.length) % options.length].focus();
    }
  }

  /* ---------- Sort ---------- */
  toggleSort() {
    const toggle = this.querySelector('[data-sort-toggle]');
    const menu = this.querySelector('[data-sort-menu]');
    if (!toggle || !menu) return;
    const open = menu.hidden;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) (menu.querySelector('[aria-selected="true"] button') || menu.querySelector('button'))?.focus();
  }

  closeSort(returnFocus = false) {
    const toggle = this.querySelector('[data-sort-toggle]');
    const menu = this.querySelector('[data-sort-menu]');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    if (returnFocus) toggle?.focus();
  }

  setSort(value) {
    const params = this.params;
    if (value) params.set('sort_by', value);
    else params.delete('sort_by');
    params.delete('page');
    this.apply(params);
  }

  /* ---------- Quick chips ---------- */
  toggleChip(spec) {
    const index = spec.indexOf('=');
    if (index < 1) return;
    const key = spec.slice(0, index);
    const value = spec.slice(index + 1);
    const params = this.params;
    const present = params.getAll(key).includes(value);
    if (present) {
      const rest = params.getAll(key).filter((v) => v !== value);
      params.delete(key);
      rest.forEach((v) => params.append(key, v));
    } else {
      if (/\.(gte|lte)$/.test(key)) params.delete(key);
      params.append(key, value);
    }
    params.delete('page');
    this.apply(params);
  }

  syncChips() {
    const params = this.params;
    this.querySelectorAll('[data-chip]').forEach((chip) => {
      const spec = chip.dataset.chip;
      const index = spec.indexOf('=');
      const active = index > 0 && params.getAll(spec.slice(0, index)).includes(spec.slice(index + 1));
      chip.setAttribute('aria-pressed', String(active));
      chip.classList.toggle('is-active', active);
    });
  }

  /* ---------- Price slider ---------- */
  syncNumbersFromSliders(changed) {
    const wrap = changed.closest('[data-price-range]');
    const minRange = wrap.querySelector('[data-range-min]');
    const maxRange = wrap.querySelector('[data-range-max]');
    let lo = Number(minRange.value);
    let hi = Number(maxRange.value);
    if (lo > hi) {
      if (changed === minRange) {
        lo = hi;
        minRange.value = lo;
      } else {
        hi = lo;
        maxRange.value = hi;
      }
    }
    const max = Number(wrap.dataset.rangeMax);
    wrap.querySelector('[data-price-min]').value = lo <= 0 ? '' : lo;
    wrap.querySelector('[data-price-max]').value = hi >= max ? '' : hi;
    // Dragging only repaints; the range's `change` event (thumb released) reaches onChange and applies.
    this.paintSlider(wrap, lo, hi);
  }

  syncSlidersFromInputs(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-price-range]').forEach((wrap) => {
      const max = Number(wrap.dataset.rangeMax);
      const minInput = wrap.querySelector('[data-price-min]');
      const maxInput = wrap.querySelector('[data-price-max]');
      let lo = minInput.value === '' ? 0 : Math.max(0, Math.min(Number(minInput.value), max));
      let hi = maxInput.value === '' ? max : Math.max(0, Math.min(Number(maxInput.value), max));
      if (lo > hi) [lo, hi] = [hi, lo];
      wrap.querySelector('[data-range-min]').value = lo;
      wrap.querySelector('[data-range-max]').value = hi;
      this.paintSlider(wrap, lo, hi);
    });
  }

  paintSlider(wrap, lo, hi) {
    const max = Number(wrap.dataset.rangeMax) || 1;
    const slider = wrap.querySelector('.otw-fprice__slider');
    slider.style.setProperty('--otw-lo', `${(lo / max) * 100}%`);
    slider.style.setProperty('--otw-hi', `${(hi / max) * 100}%`);
  }

  /* ---------- Mobile filter drawer ---------- */
  selectGroup(tab, focus = false) {
    this.querySelectorAll('[data-group-tab]').forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const pane = this.querySelector(`#${t.getAttribute('aria-controls')}`);
      if (pane) pane.hidden = !selected;
    });
    if (focus) tab.focus();
  }

  clearStaged() {
    const form = this.querySelector('[data-facet-form="drawer"]');
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]').forEach((box) => (box.checked = false));
    form.querySelectorAll('[data-price-min], [data-price-max]').forEach((input) => (input.value = ''));
    this.syncSlidersFromInputs(form);
    this.refreshStaged();
  }

  refreshStaged({ fetchCount = true } = {}) {
    const form = this.querySelector('[data-facet-form="drawer"]');
    if (!form) return;
    const tabs = Array.from(form.querySelectorAll('[data-group-tab]'));
    const panes = Array.from(form.querySelectorAll('[data-group-pane]'));
    panes.forEach((pane, i) => {
      let count = pane.querySelectorAll('input[type="checkbox"]:checked').length;
      const range = pane.querySelector('[data-price-range]');
      if (range) {
        const lo = range.querySelector('[data-price-min]').value;
        const hi = range.querySelector('[data-price-max]').value;
        count += lo !== '' || hi !== '' ? 1 : 0;
      }
      const badge = tabs[i]?.querySelector('[data-group-badge]');
      if (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
      }
    });
    this.setApplyLabel(fetchCount ? null : this.total);
    if (fetchCount) this.fetchStagedCount(form);
  }

  setApplyLabel(n) {
    const button = this.querySelector('[data-facet-apply]');
    if (!button) return;
    button.textContent = n == null ? button.textContent : this.dataset.applyLabel.replace('__n__', n);
  }

  async fetchStagedCount(form) {
    this.countController?.abort();
    const controller = new AbortController();
    this.countController = controller;
    const params = this.paramsFromForm(form);
    try {
      const node = await window.OTW.fetchSection(
        `${window.location.pathname}?${params}`,
        this.dataset.countSection || 'otw-facet-count',
        { signal: controller.signal }
      );
      const value = node?.querySelector('[data-otw-count]')?.textContent.trim();
      if (controller === this.countController && value !== undefined) this.setApplyLabel(value);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('otw-facets: count failed', error);
    }
  }

  /* ---------- Rendering ---------- */
  gridList() {
    return this.querySelector('[data-region="grid"] [data-grid]');
  }

  showSkeleton({ append = false } = {}) {
    const region = this.querySelector('[data-region="grid"]');
    const template = this.querySelector('template[data-skeleton]');
    if (!region || !template) return;
    region.setAttribute('aria-busy', 'true');
    region.classList.add('is-loading');
    let list = this.gridList();
    if (!list) {
      list = document.createElement('ul');
      list.className = 'otw-listing__grid';
      list.setAttribute('role', 'list');
      list.dataset.grid = '';
      region.replaceChildren(list);
    }
    const fragment = template.content.cloneNode(true);
    fragment.querySelectorAll('li').forEach((li) => li.setAttribute('data-skeleton-item', ''));
    if (!append) list.replaceChildren();
    list.append(fragment);
  }

  clearSkeleton() {
    const region = this.querySelector('[data-region="grid"]');
    region?.setAttribute('aria-busy', 'false');
    region?.classList.remove('is-loading');
    this.querySelectorAll('[data-skeleton-item]').forEach((el) => el.remove());
  }

  swapRegions(section) {
    const open = new Map();
    this.querySelectorAll('.otw-fgroup').forEach((group) => {
      open.set(group.dataset.fgroup, group.querySelector('.otw-accordion__trigger')?.getAttribute('aria-expanded'));
    });
    const expanded = Array.from(this.querySelectorAll('.otw-fvals.is-expanded')).map(
      (list) => list.closest('.otw-fgroup')?.dataset.fgroup
    );

    this.querySelectorAll('[data-region]').forEach((region) => {
      const next = section.querySelector(`[data-region="${region.dataset.region}"]`);
      if (!next) return;
      region.innerHTML = next.innerHTML;
      if (next.dataset.total !== undefined) region.dataset.total = next.dataset.total;
    });

    this.querySelectorAll('.otw-fgroup').forEach((group) => {
      const state = open.get(group.dataset.fgroup);
      if (state) group.querySelector('.otw-accordion__trigger')?.setAttribute('aria-expanded', state);
      if (expanded.includes(group.dataset.fgroup)) {
        const list = group.querySelector('.otw-fvals');
        const button = group.querySelector('[data-fmore]');
        list?.classList.add('is-expanded');
        if (button) {
          button.setAttribute('aria-expanded', 'true');
          button.textContent = button.dataset.less;
        }
      }
    });

    const text = section.querySelector('[data-region="count-text"]')?.textContent.trim();
    if (text) document.querySelectorAll('[data-otw-count-label]').forEach((el) => (el.textContent = text));
  }

  async apply(params, { push = true, scroll = false, force = false } = {}) {
    const query = params.toString();
    const target = `${window.location.pathname}${query ? `?${query}` : ''}`;
    if (!force && push && target === `${window.location.pathname}${window.location.search}`) return;

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.infiniteObserver?.disconnect();
    this.closeSort();
    this.showSkeleton();

    try {
      const section = await window.OTW.fetchSection(target, this.sectionId, { signal: controller.signal });
      if (controller !== this.controller) return;
      this.swapRegions(section);
      if (push) window.history.pushState({ otwFacets: true }, '', target);
      this.syncChips();
      this.observeInfinite();
      if (scroll || mobileQuery.matches) this.scrollToGrid();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('otw-facets: falling back to navigation', error);
      window.location.href = target;
    } finally {
      if (controller === this.controller) this.clearSkeleton();
    }
  }

  scrollToGrid() {
    const top = this.querySelector('.otw-listing__main');
    if (!top) return;
    const behavior = window.OTW?.reducedMotion?.() ? 'auto' : 'smooth';
    top.scrollIntoView({ block: 'start', behavior });
  }

  /* ---------- Load more / infinite ---------- */
  async loadMore() {
    if (this.loading) return;
    const button = this.querySelector('[data-load-more]');
    const list = this.gridList();
    if (!button || !list) return;
    this.loading = true;
    this.infiniteObserver?.disconnect();
    const page = Number(button.dataset.nextPage);
    const params = this.params;
    params.set('page', page);
    const target = `${window.location.pathname}?${params}`;
    button.setAttribute('aria-busy', 'true');
    this.showSkeleton({ append: true });

    try {
      const section = await window.OTW.fetchSection(target, this.sectionId);
      const nextList = section.querySelector('[data-region="grid"] [data-grid]');
      this.querySelectorAll('[data-skeleton-item]').forEach((el) => el.remove());
      nextList?.querySelectorAll(':scope > li').forEach((item) => list.append(item));
      const pagination = section.querySelector('[data-region="pagination"]');
      this.querySelector('[data-region="pagination"]').innerHTML = pagination ? pagination.innerHTML : '';
      window.history.replaceState({ otwFacets: true }, '', target);
    } catch (error) {
      console.warn('otw-facets: load more failed', error);
      this.querySelectorAll('[data-skeleton-item]').forEach((el) => el.remove());
      window.location.href = target;
    } finally {
      this.clearSkeleton();
      button.removeAttribute('aria-busy');
      this.loading = false;
      this.observeInfinite();
    }
  }

  observeInfinite() {
    this.infiniteObserver?.disconnect();
    if (this.mode !== 'infinite' || !('IntersectionObserver' in window)) return;
    const button = this.querySelector('[data-load-more]');
    if (!button) return;
    this.infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { rootMargin: '600px 0px' }
    );
    this.infiniteObserver.observe(button);
  }

  /* ---------- Sticky mobile bar follows the header ---------- */
  watchHeader() {
    const header = document.querySelector('otw-header');
    if (!header || document.documentElement.dataset.otwHeaderWatch) return;
    document.documentElement.dataset.otwHeaderWatch = 'true';
    const update = () => {
      document.documentElement.toggleAttribute('data-otw-header-hidden', header.classList.contains('is-hidden'));
    };
    this.headerObserver = new MutationObserver(update);
    this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    update();
  }
}

if (!customElements.get('otw-facets')) customElements.define('otw-facets', OtwFacets);

/* ---------- Search tab counts (tabs that were not part of this response) ---------- */
function loadTabCounts() {
  document.querySelectorAll('[data-tab-count-url]').forEach(async (tab) => {
    const label = tab.querySelector('[data-tab-count]');
    if (!label || label.textContent.trim() !== '') return;
    try {
      const node = await window.OTW.fetchSection(tab.dataset.tabCountUrl, 'otw-facet-count');
      const value = node?.querySelector('[data-otw-count]')?.textContent.trim();
      if (value !== undefined) label.textContent = value;
    } catch (error) {
      console.warn('otw-facets: tab count failed', error);
    }
  });
}

loadTabCounts();
